import {inject} from '@angular/core';
import {
  catchError,
  finalize,
  map,
  mergeMap,
  of,
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitPlanActions, VisitPlanAPiActions, VisitPlanChannelActions} from "./visit-plan.actions";
import {EntityPaginationModel} from "../../../../commons/models/entity-pagination.model";
import {VisitPlanChannelService} from "../../services/visit-plan-channel.service";
import {VisitScheduleAPiActions} from "../visit-plan-schedule-state/visit-plan-schedule.actions";
import {ToastService} from "../../../../commons/angular/services/toast.service";

export const getVisitPlan = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.getVisitPlan),
      mergeMap(payload => {
        return visitPlanHttpService.getVisitPlan(payload.visitPlanId).pipe(
          map((visitPlan: VisitPlanModel) => VisitPlanAPiActions.getVisitPlanSuccess({
            visitPlan: visitPlan,
            isSubscribeToChannel: payload.isSubscribeToChannel
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAPiActions.getVisitPlanFailure({error})))
        )
      })
    );
  },
  {functional: true}
);

export const getVisitPlans = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.getVisitPlans),
      mergeMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return visitPlanHttpService.queryVisitPlans(payload.filter || {}, payload.params).pipe(
          map((res: EntityPaginationModel<VisitPlanModel>) => VisitPlanAPiActions.getVisitPlansSuccess({
            visitPlans: res.content,
            isSubscribeToChannel: payload.isSubscribeToChannel
          })),
          catchError((error: {
            message: string
          }) => of(VisitPlanAPiActions.getVisitPlansFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getVisitPlanSuccess = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.getVisitPlanSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          visitPlanChannelService.subscribeById(payload.visitPlan.id);
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const setVisitPlan = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanActions.setVisitPlan),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          visitPlanChannelService.subscribeById(payload.visitPlan.id);
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const getVisitPlansSuccess = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.getVisitPlansSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const visitPlanIds = payload.visitPlans.map(v => v.id);
          visitPlanIds.forEach(visitPlanId => visitPlanChannelService.subscribeById(visitPlanId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);


export const subscribeSetVisitPlansChannels = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanActions.setVisitPlans),
      map((payload) => {
        const visitPlanIds = payload.visitPlans.map(v => v.id);
        visitPlanIds.forEach(visitPlanId => visitPlanChannelService.subscribeById(visitPlanId));
      })
    );
  },
  {functional: true, dispatch: false}
);

export const deleteVisitPlan = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService),
   toastService = inject(ToastService),
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.deleteVisitPlan),
      mergeMap(payload => {
          commonsFacadeService.setLoaderOn();
          return visitPlanHttpService.deleteVisitPlan(payload.visitPlanId, payload.force).pipe(
            map(() => {
              toastService.success('VISIT_PLAN_DELETE_SUCCESS');
              return VisitPlanAPiActions.deleteVisitPlanSuccess({
                visitPlanId: payload.visitPlanId
              })
            }),
            catchError((error: { message: string }) => {
              toastService.error('VISIT_PLAN_DELETE_ERROR');
              return of(VisitPlanAPiActions.getVisitPlansFailure({error}))
            }),
            finalize(() => {
              commonsFacadeService.setLoaderOff()
            })
          )
        }
      )
    );
  },
  {functional: true}
);

export const restoreVisitPlan = createEffect(
  (actions$ = inject(Actions),
   visitPlanHttpService = inject(VisitPlanHttpService),
   commonsFacadeService: CommonsFacadeService = inject(CommonsFacadeService),
   toastService = inject(ToastService),
  ) => {
    return actions$.pipe(
      ofType(VisitPlanAPiActions.restoreVisitPlan),
      mergeMap(payload => {
          commonsFacadeService.setLoaderOn();
          return visitPlanHttpService.restoreVisitPlan(payload.visitPlanId).pipe(
            map(() => {
              toastService.success('VISIT_PLAN_RESTORE_SUCCESS');
              return VisitPlanAPiActions.restoreVisitPlanSuccess({
                visitPlanId: payload.visitPlanId
              })
            }),
            catchError((error: { message: string }) => {
              toastService.error('VISIT_PLAN_RESTORE_ERROR');
              return of(VisitPlanAPiActions.restoreVisitPlanFailure({error}))
            }),
            finalize(() => {
              commonsFacadeService.setLoaderOff()
            })
          )
        }
      )
    );
  },
  {functional: true}
);

export const subscribeAllVisitPlansChannel = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanChannelActions.subscribe),
      mergeMap(payload =>
        visitPlanChannelService.getChannelsValues().pipe(
          map((notification) => {
            return VisitPlanChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const unsubscribeAllVisitPlanChannels = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanChannelActions.unsubscribe, VisitPlanActions.removeAll, VisitPlanActions.clearStore),
      map(payload => {
          visitPlanChannelService.unSubscribeAll();
          return VisitPlanChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);


export const unsubscribeVisitPlanChannels = createEffect(
  (actions$ = inject(Actions),
   visitPlanChannelService = inject(VisitPlanChannelService)) => {
    return actions$.pipe(
      ofType(VisitPlanActions.removeVisitPlans),
      map(payload => {
          payload.ids?.forEach(serviceId => visitPlanChannelService.unSubscribeById(serviceId));
          return VisitPlanChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);
